import type { AIEvents } from './AI'
import type { OracleEvents } from './Oracle'
import type { VideoPlayerEvents } from './VideoPlayer'
import type { CommunityEvents } from './Community'

/**
 * Maps event names to their callback signatures. Each event defines the
 * arguments its tracking callback accepts. The hook handles building full
 * properties from these minimal args.
 *
 * Note the naming convention: these names are established by Product.
 * Generally, <domain>_<noun>_<verb>, sometimes the domain/noun are rolled into
 * one.
 *
 * Event signatures are split per-feature under `events/<Feature>/`. Each
 * feature folder mirrors a `lib/components/<Feature>/` surface — add new event
 * signatures in the folder of the feature that fires them. Event names MUST be
 * globally unique across all slices.
 */
export type PostHogEventSignatures = AIEvents & OracleEvents & VideoPlayerEvents & CommunityEvents

/**
 * Union of all type-safe event names.
 * Use with usePostHogEvent() for autocomplete and type checking.
 */
export type PostHogEventName = keyof PostHogEventSignatures

// Re-export per-feature unions for callers that want feature-scoped types
export type { AIEvents } from './AI'
export type { OracleEvents } from './Oracle'
export type { VideoPlayerEvents } from './VideoPlayer'
export type { CommunityEvents } from './Community'

// Re-export commonly-used helper types
export type {
  AiInputMethod,
  AiSubmissionMethod,
  AiFeedbackUiElement,
  AiRecommendationPayload,
} from './AI'
// Runtime const (canonical Title Case input_method values) — value, not type.
export { AI_INPUT_METHOD } from './AI'
export type { DivinationChannel } from './Oracle'
export type {
  CommunityHeaderLinkSource,
  CommunityLibraryTab,
  CommunityPlaylistType,
  CommunityPlaylistCreationMethod,
  CommunityPlaylistCreatedDetails,
  CommunityPlaylistSmartCreateSource,
  CommunityPlaylistItemEntryPoint,
  CommunityPlaylistOwnership,
  CommunityShareMethod,
  CommunityConnectionsTab,
  CommunityProfileVisibility,
  CommunityDiscoverSearchState,
} from './Community'
